import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { IconCircleCheck } from "@tabler/icons-react";
import type { UserDtoDocumentsInnerTypeEnum } from "../../../../../../api";
import {
  CreatePartnerDtoPartnerTypeEnum,
  type CreateDocumentDto,
  type CreateUploadUrlDto,
} from "../../../../../../api";
import { useRegisterWizard } from "../../../../domain/register-wizard";
import { AppRoutes } from "../../../../../../common/routes/app_routes";
import { Button } from "../../../../../../components/common/button";
import { FileDropzoneCard } from "../../../../../../components/common/file-dropzone-card";
import { FileUploadItem } from "../../../../../../components/common/file-upload-item";
import { Heading, Text } from "../../../../../../components/common/typography";
import { documentsService } from "../../../../../api_client";
import { showSuccessToast } from "../../../../../../utils/toasts";
import { handleAxiosError } from "../../../../../../common/error_handling";
import RegisterWizardLayout from "./register-wizard-layout";
import AuthSplitLayout from "../../../../routes/auth-split-layout";

type DocType = UserDtoDocumentsInnerTypeEnum;
type UploadState = "idle" | "uploading" | "uploaded" | "failed";

const DOC_TYPES: DocType[] = ["CR", "TAX_CARD"];

/** Mirrors the backend's DOCUMENT_MAX_BYTES. */
const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

const DOC_TITLE: Record<DocType, string> = {
  CR: "Commercial Registration",
  TAX_CARD: "Tax Card",
};

const initialPerType = <T,>(value: T): Record<DocType, T> =>
  ({ CR: value, TAX_CARD: value }) as Record<DocType, T>;

export function DocumentsStep() {
  const navigate = useNavigate();
  const { partnerType } = useRegisterWizard();
  const isSeller = partnerType === CreatePartnerDtoPartnerTypeEnum.Seller;
  const nextRoute = isSeller
    ? AppRoutes.CREATE_ACCOUNT_BANKING_DETAILS
    : AppRoutes.CREATE_ACCOUNT_APPROVAL_PENDING;

  const [files, setFiles] = useState<Record<DocType, File | null>>(() =>
    initialPerType<File | null>(null),
  );
  const [uploadProgress, setUploadProgress] = useState<Record<DocType, number>>(
    () => initialPerType(0),
  );
  const [uploadState, setUploadState] = useState<Record<DocType, UploadState>>(
    () => initialPerType<UploadState>("idle"),
  );
  const [uploadError, setUploadError] = useState<
    Record<DocType, string | undefined>
  >(() => initialPerType<string | undefined>(undefined));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const allSelected = DOC_TYPES.every((t) => files[t] != null);
  const anyUploading = DOC_TYPES.some((t) => uploadState[t] === "uploading");
  const allUploaded = DOC_TYPES.every((t) => uploadState[t] === "uploaded");

  const handleSelect = (type: DocType) => (file: File) => {
    setFiles((prev) => ({ ...prev, [type]: file }));
    setUploadProgress((prev) => ({ ...prev, [type]: 0 }));
    setUploadState((prev) => ({ ...prev, [type]: "idle" }));
    setUploadError((prev) => ({ ...prev, [type]: undefined }));
  };

  const handleRemove = (type: DocType) => () => {
    setFiles((prev) => ({ ...prev, [type]: null }));
    setUploadProgress((prev) => ({ ...prev, [type]: 0 }));
    setUploadState((prev) => ({ ...prev, [type]: "idle" }));
    setUploadError((prev) => ({ ...prev, [type]: undefined }));
  };

  /** Returns true on success, false on failure (state is updated either way). */
  const uploadOne = async (type: DocType, file: File): Promise<boolean> => {
    setUploadState((prev) => ({ ...prev, [type]: "uploading" }));
    setUploadError((prev) => ({ ...prev, [type]: undefined }));
    setUploadProgress((prev) => ({ ...prev, [type]: 0 }));
    try {
      const urlReq: CreateUploadUrlDto = { type, contentTypes: [file.type] };
      const urlResp = await documentsService.createDocumentUploadUrl(urlReq);
      const uploadUrlData = urlResp.data[0];
      if (!uploadUrlData) throw new Error("Failed to get upload URL");

      const headers: Record<string, string> = {
        ...(uploadUrlData.headers as Record<string, string>),
      };
      if (!headers["Content-Type"]) headers["Content-Type"] = file.type;

      await axios.put(uploadUrlData.uploadUrl, file, {
        headers,
        onUploadProgress: (event) => {
          const total = event.total;
          if (!total) return;
          setUploadProgress((prev) => ({
            ...prev,
            [type]: event.loaded / total,
          }));
        },
      });

      const docReq: CreateDocumentDto = {
        type,
        objectKey: uploadUrlData.objectKey,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      };
      await documentsService.createDocument(docReq);

      setUploadProgress((prev) => ({ ...prev, [type]: 1 }));
      setUploadState((prev) => ({ ...prev, [type]: "uploaded" }));
      return true;
    } catch (error) {
      setUploadState((prev) => ({ ...prev, [type]: "failed" }));
      setUploadError((prev) => ({
        ...prev,
        [type]: handleAxiosError(error).message,
      }));
      return false;
    }
  };

  const submit = async () => {
    if (!allSelected || submitting || anyUploading) return;
    setSubmitting(true);
    try {
      const results: Record<DocType, boolean> = {
        CR: uploadState.CR === "uploaded",
        TAX_CARD: uploadState.TAX_CARD === "uploaded",
      };
      for (const type of DOC_TYPES) {
        if (results[type]) continue;
        const file = files[type];
        if (!file) continue;
        results[type] = await uploadOne(type, file);
      }
      if (DOC_TYPES.every((t) => results[t])) {
        showSuccessToast("Documents submitted for review.");
        if (isSeller) {
          setSubmitted(true);
        } else {
          navigate(nextRoute);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = (type: DocType) => async () => {
    const file = files[type];
    if (!file) return;
    const otherTypes = DOC_TYPES.filter((t) => t !== type);
    const othersDone = otherTypes.every((t) => uploadState[t] === "uploaded");
    const ok = await uploadOne(type, file);
    if (ok && othersDone) {
      showSuccessToast("Documents submitted for review.");
      if (isSeller) {
        setSubmitted(true);
      } else {
        navigate(nextRoute);
      }
    }
  };

  const anyFailed = DOC_TYPES.some((t) => uploadState[t] === "failed");
  const submitLabel = submitting
    ? "Submitting…"
    : anyFailed
      ? "Retry submission"
      : allUploaded
        ? "Submitted"
        : "Submit documents";

  if (submitted) {
    return (
      <AuthSplitLayout>
        <div className="mt-[25vh] flex w-full flex-col items-center gap-6 text-center">
          <span className="inline-flex size-16 items-center justify-center text-accentGreen">
            <IconCircleCheck size={64} />
          </span>
          <Heading variant="h2" tone="dark">
            Documents submitted
          </Heading>
          <Text variant="body" tone="body">
            Your documents have been submitted for review. Next, add your
            banking details so we can process payments.
          </Text>
        </div>
        <Button
          variant="primary"
          fullWidth
          onClick={() => navigate(AppRoutes.CREATE_ACCOUNT_BANKING_DETAILS)}
        >
          Continue to banking details
        </Button>
      </AuthSplitLayout>
    );
  }

  return (
    <RegisterWizardLayout
      title="Required documents"
      subtitle="Upload both documents to verify your business. Our team will review them before granting access."
      progress={{ current: 3, total: isSeller ? 4 : 3 }}
    >
      {DOC_TYPES.map((type) => {
        const file = files[type];
        if (!file) {
          return (
            <FileDropzoneCard
              key={type}
              title={DOC_TITLE[type]}
              maxBytes={DOCUMENT_MAX_BYTES}
              onFileSelect={handleSelect(type)}
            />
          );
        }
        const state = uploadState[type];
        return (
          <FileUploadItem
            key={type}
            status={state}
            title={DOC_TITLE[type]}
            fileName={file.name}
            fileSize={file.size}
            progress={uploadProgress[type]}
            errorMessage={uploadError[type]}
            onRemove={
              submitting || state === "uploading"
                ? undefined
                : handleRemove(type)
            }
            onRetry={state === "failed" ? handleRetry(type) : undefined}
          />
        );
      })}

      <Button
        variant="primary"
        fullWidth
        onClick={submit}
        disabled={!allSelected || submitting || anyUploading || allUploaded}
      >
        {submitLabel}
      </Button>
    </RegisterWizardLayout>
  );
}

export default DocumentsStep;
